import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { Select, MenuItem } from '@mui/material';
import moment from 'moment';
import {
    Box,
    Button,
    CardContent,
    Grid,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    TextField,
    Toolbar,
    Typography
} from '@mui/material';

import MainCard from 'ui-component/cards/MainCard';
import { visuallyHidden } from '@mui/utils';

import SearchIcon from '@mui/icons-material/Search';

import { useTheme } from '@mui/material/styles';

// Dummy data
const dummyOrders = [
    {
        taiLieu: 'Hướng dẫn sử dụng.pdf',
        moTa: 'Mô tả cho tài liệu 1',
        thoiGianCapNhat: '2023-01-01'
    },
    {
        taiLieu: 'Văn bản ban hành.pdf',
        moTa: 'Mô tả cho tài liệu 2',
        thoiGianCapNhat: '2023-11-15'
    }
];

const options = [
    { label: 'Tất cả thời gian', value: 'all' },
    { label: '7 ngày trước', value: '7' },
    { label: '30 ngày trước', value: '30' },
    { label: '90 ngày trước', value: '90' }
];

const sortOptions = [
    { label: 'Xếp theo mới nhất', value: 'desc' },
    { label: 'Xếp theo cũ nhất', value: 'asc' }
];
function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

const getComparator = (order, orderBy) =>
    order === 'desc' ? (a, b) => descendingComparator(a, b, orderBy) : (a, b) => -descendingComparator(a, b, orderBy);

function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

const headCells = [
    { id: 'taiLieu', label: 'Tài liệu', align: 'left' },
    { id: 'moTa', label: 'Mô tả', align: 'left' }, // Add this line
    { id: 'thoiGianCapNhat', label: 'Thời gian cập nhật', align: 'left' }
];

const EnhancedTableHead = ({ onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort, theme, selected }) => {
    const createSortHandler = (property) => (event) => {
        onRequestSort(event, property);
    };

    return (
        <TableHead>
            <TableRow>
                {numSelected > 0 && (
                    <TableCell padding="none" colSpan={8}>
                        <EnhancedTableToolbar numSelected={selected.length} />
                    </TableCell>
                )}
                {numSelected <= 0 &&
                    headCells.map((headCell) => (
                        <TableCell
                            key={headCell.stt}
                            align={headCell.align}
                            padding={headCell.disablePadding ? 'none' : 'normal'}
                            sortDirection={orderBy === headCell.stt ? order : false}
                        >
                            <TableSortLabel
                                active={orderBy === headCell.stt}
                                direction={orderBy === headCell.stt ? order : 'asc'}
                                onClick={createSortHandler(headCell.stt)}
                                sx={{
                                    // fontSize: '0.1rem',
                                    fontWeight: 'bold'
                                }}
                            >
                                {headCell.label}
                                {orderBy === headCell.stt ? (
                                    <Box component="span" sx={visuallyHidden}>
                                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                    </Box>
                                ) : null}
                            </TableSortLabel>
                        </TableCell>
                    ))}
            </TableRow>
        </TableHead>
    );
};

EnhancedTableHead.propTypes = {
    theme: PropTypes.object,
    selected: PropTypes.array,
    numSelected: PropTypes.number.isRequired,
    onRequestSort: PropTypes.func.isRequired,
    onSelectAllClick: PropTypes.func.isRequired,
    order: PropTypes.oneOf(['asc', 'desc']).isRequired,
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired
};

const EnhancedTableToolbar = ({ numSelected }) => (
    <Toolbar
        sx={{
            p: 0,
            pl: 1,
            pr: 1,
            ...(numSelected > 0 && {
                color: (theme) => theme.palette.secondary.main
            })
        }}
    >
        {numSelected > 0 ? (
            <Typography color="inherit" variant="h4">
                Đã chọn {numSelected}
            </Typography>
        ) : (
            <Typography variant="h6" id="tableTitle">
                Nutrition
            </Typography>
        )}
        <Box sx={{ flexGrow: 1 }} />
        {/* {numSelected > 0 && (
      <Tooltip title="Delete">
        <IconButton size="large" onClick={() => selected.forEach(taiLieu => handleDelete(taiLieu))}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    )} */}
    </Toolbar>
);

EnhancedTableToolbar.propTypes = {
    numSelected: PropTypes.number.isRequired
};

const LogComponent = ({ onBack }) => {
    const [logOpen, setLogOpen] = useState(false);
    const [originalRows, setOriginalRows] = useState(dummyOrders);
    const [open, setOpen] = useState(false);
    const [sort, setSort] = useState('desc');
    const [timeRange, setTimeRange] = useState('all');
    const theme = useTheme();
    const dispatch = () => {};
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('calories');
    const [selected, setSelected] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [search, setSearch] = useState('');
    const [rows, setRows] = useState(dummyOrders);

    const filterDataByTimeRange = (range) => {
        const currentDate = moment();

        if (range === '7') {
            const sevenDaysAgo = currentDate.clone().subtract(7, 'days');
            return dummyOrders.filter(
                (row) => moment(row.thoiGianCapNhat).isAfter(sevenDaysAgo) || moment(row.thoiGianCapNhat).isSame(sevenDaysAgo)
            );
        } else if (range === '30') {
            const thirtyDaysAgo = currentDate.clone().subtract(30, 'days');
            return dummyOrders.filter(
                (row) => moment(row.thoiGianCapNhat).isAfter(thirtyDaysAgo) || moment(row.thoiGianCapNhat).isSame(thirtyDaysAgo)
            );
        } else if (range === '90') {
            const ninetyDaysAgo = currentDate.clone().subtract(90, 'days');
            return dummyOrders.filter(
                (row) => moment(row.thoiGianCapNhat).isAfter(ninetyDaysAgo) || moment(row.thoiGianCapNhat).isSame(ninetyDaysAgo)
            );
        } else if (range === 'all') {
            return dummyOrders;
        } else {
            return [];
        }
    };

    const handleFilter = () => {
        if (timeRange === 'all') {
            setRows(originalRows);
        } else {
            const filteredRows = filterDataByTimeRange(timeRange);
            setRows(filteredRows);
        }
    };
    useEffect(() => {
        handleFilter();
    }, [timeRange, originalRows, rows]);

    const handleTimeRangeChange = (e) => {
        setTimeRange(e.target.value);
        setRows(originalRows);
    };

    const handleDelete = (taiLieu) => {
        // Lọc ra các hàng không chứa taiLieu cần xóa
        const newRows = rows.filter((row) => row.taiLieu !== taiLieu);
        setOriginalRows(newRows);

        // Xóa khỏi mảng đã chọn
        setSelected(selected.filter((selectedTaiLieu) => selectedTaiLieu !== taiLieu));
    };
    const handleSearch = (event) => {
        const newString = event?.target.value;
        setSearch(newString || '');

        if (newString) {
            const newRows = dummyOrders.filter((row) => {
                let matches = true;

                const properties = ['taiLieu', 'moTa', 'thoiGianCapNhat'];
                let containsQuery = false;

                properties.forEach((property) => {
                    if (row[property].toString().toLowerCase().includes(newString.toString().toLowerCase())) {
                        containsQuery = true;
                    }
                });

                if (!containsQuery) {
                    matches = false;
                }
                return matches;
            });
            setOriginalRows(newRows);
        } else {
            setOriginalRows(dummyOrders);
        }
    };

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            if (selected.length > 0) {
                setSelected([]);
            } else {
                const newSelectedId = dummyOrders.map((n) => n.taiLieu);
                setSelected(newSelectedId);
            }
            return;
        }
        setSelected([]);
    };

    const handleClick = (event, taiLieu) => {
        const selectedIndex = selected.indexOf(taiLieu);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, taiLieu);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
        }

        setSelected(newSelected);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event?.target.value, 10));
        setPage(0);
    };

    const isSelected = (taiLieu) => selected.indexOf(taiLieu) !== -1;
    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

    const toggleLog = () => {
        setLogOpen(!logOpen);
    };

    return (
        <MainCard title="Tất cả tài liệu / Tài liệu trợ lý A / Nhật ký tài liệu" content={false}>
            <CardContent>
                <Grid container sx={{ justifyContent: 'flex-end' }}>
                    <Grid item lg={3}>
                        <Button variant="outlined" color="secondary" onClick={onBack}>
                            Trở về
                        </Button>
                    </Grid>
                    <Grid item lg={9} sx={{ textAlign: 'right' }}>
                        <Select value={timeRange} onChange={handleTimeRangeChange} sx={{ width: '200px', marginRight: '10px' }}>
                            {options.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>

                        <Select
                            value={sort}
                            onChange={(e) => {
                                setSort(e.target.value);
                                handleRequestSort(null, 'thoiGianCapNhat');
                            }}
                            sx={{ width: '200px', marginRight: '10px' }}
                        >
                            {sortOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                        <TextField
                            sx={{ width: '300px', marginRight: '0' }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="medium" />
                                    </InputAdornment>
                                )
                            }}
                            onChange={handleSearch}
                            placeholder="Tìm kiếm"
                            value={search}
                            size="medium"
                        />
                    </Grid>
                </Grid>
            </CardContent>

            <TableContainer>
                <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle">
                    <EnhancedTableHead
                        numSelected={selected.length}
                        order={order}
                        orderBy={orderBy}
                        onSelectAllClick={handleSelectAllClick}
                        onRequestSort={handleRequestSort}
                        rowCount={rows.length}
                        theme={theme}
                        selected={selected}
                    />
                    <TableBody>
                        {stableSort(rows, getComparator(order, orderBy))
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((row, index) => {
                                const labelId = `enhanced-table-checkbox-${index}`;

                                return (
                                    <TableRow>
                                        <TableCell>{row.taiLieu}</TableCell>
                                        <TableCell>{row.moTa}</TableCell>
                                        <TableCell>{row.thoiGianCapNhat}</TableCell>
                                    </TableRow>
                                );
                            })}
                        {emptyRows > 0 && (
                            <TableRow
                                style={{
                                    height: 53 * emptyRows
                                }}
                            >
                                <TableCell colSpan={6} />
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={rows.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </MainCard>
    );
};

export default LogComponent;
